// Italian (tu) copy for /on-demand-ftp, translated from copy.en.ts. Same shape (checked by copy.test.ts).
// "FTP su richiesta" as in the site's footer (src/i18n/locales/it.json); "l'uploader web" for the web uploader.
// The uploader's and FileZilla's own labels are in English, so «labels» are quoted as the app shows them.
import type { OnDemandFtpCopy } from "./copy.en";

const it: OnDemandFtpCopy = {
  seo: {
    title: "Come funziona l'FTP su richiesta | Photohawk",
    description:
      "Se l'FTP è indispensabile, chiunque nella tua area di lavoro può avviare un proprio server FTP Photohawk dall'uploader web, con tutti i piani. È pronto in circa 30 secondi, ha un indirizzo personale che non cambia mai e si spegne da solo dopo un'ora di inattività.",
  },
  hero: {
    badge: "Tutti i piani",
    eyebrow: "Disponibile ora",
    title: "Come funziona l'FTP su richiesta",
    subtitle:
      "Se l'FTP è indispensabile, avvia il tuo server FTP dall'uploader web ogni volta che ti serve. È pronto in circa 30 secondi e si spegne da solo quando hai finito.",
  },
  before: {
    title: "Prima di iniziare: l'uploader web è più veloce",
    body: "L'uploader web è il modo standard per caricare, con tutti i piani.",
    points: [
      { id: "parallel", text: "Invia molte foto alla volta, molte più di un programma FTP come FileZilla (2 alla volta di default, e non più di 10)." },
      { id: "adaptive", text: "Si adatta alla tua connessione, per sfruttarla al massimo." },
      { id: "live", text: "Le anteprime partono per prime, così la tua galleria è online in pochi minuti mentre arrivano gli originali." },
      { id: "resume", text: "Se la connessione cade, riprende da solo." },
    ],
    button: "Apri l'uploader web",
    link: "Scopri il nuovo uploader web",
  },
  what: {
    heading: "Il tuo server FTP, quando ti serve",
    body: "Chiunque nella tua area di lavoro può avviare un proprio server FTP dall'uploader web, con tutti i piani. Ognuno ha il suo server, con un indirizzo personale che non cambia mai, così configuri il tuo programma FTP una volta sola. Quando non lo usi, non resta acceso: si spegne da solo dopo un'ora di inattività.",
    date: "1° novembre 2026",
    closing: "I nostri server FTP condivisi sempre attivi chiudono il 1° novembre 2026. Li usi oggi? Avvia il tuo server e cambia l'host nel tuo programma FTP.",
  },
  steps: {
    heading: "Come funziona",
    subtitle: "Quattro passi la prima volta. Poi basta avviare il server e caricare.",
    items: [
      { id: "open", title: "Apri l'uploader web", body: "Vai su `upload.photohawk.com` e accedi, oppure apri la scheda «Upload» di una galleria." },
      { id: "start", title: "Avvia il tuo server", body: "Fai clic su «FTP server» in alto, poi su «Start FTP server». È pronto in circa 30 secondi." },
      { id: "connect", title: "Copia indirizzo e credenziali nel tuo programma FTP", body: "Il tuo indirizzo, la porta 21, l'email e la password di Photohawk. L'indirizzo non cambia mai." },
      { id: "upload", title: "Carica", body: "Aggiungi le tue foto nella cartella della galleria. Il server si spegne dopo un'ora senza caricamenti." },
    ],
    button: "Avvia il tuo server FTP",
    buttonNote: "Apre l'uploader web con il tuo server FTP pronto da avviare.",
  },
  panel: {
    heading: "Cosa vedrai",
    body: "Trovi tutto nel pannello «Your FTP server» dell'uploader web, con un pulsante per copiare accanto a ogni dato.",
    points: [
      { id: "address", text: "Il tuo indirizzo, ad esempio `swift-heron-4821.ftp.photohawk.com`. È tuo per sempre, quindi lo configuri una volta sola." },
      { id: "ip", text: "Viene mostrato anche l'indirizzo IP del server. Cambia ogni volta che avvii il server, quindi quando puoi usa l'indirizzo." },
      { id: "countdown", text: "Un conto alla rovescia fino allo spegnimento, con «Keep it running» e «Stop»." },
    ],
    alt: "Il pannello “Your FTP server” con lo stato Ready in Europa (Irlanda), l'host swift-heron-4821.ftp.photohawk.com, l'indirizzo IP, la porta 21, il nome utente alex@example.com, la modalità passiva e l'FTP su TLS, e un conto alla rovescia con i pulsanti Keep it running e Stop.",
  },
  details: {
    heading: "I tuoi dati di connessione",
    subtitle: "Inseriscili nel tuo programma FTP.",
    rows: [
      { id: "host", label: "Host", value: "Il tuo indirizzo, ad es. `swift-heron-4821.ftp.photohawk.com` (o l'indirizzo IP mostrato)" },
      { id: "port", label: "Porta", value: "21" },
      { id: "user", label: "Nome utente", value: "La tua email di Photohawk" },
      { id: "password", label: "Password", value: "La tua password di Photohawk o la tua password FTP (vedi sotto)" },
      { id: "mode", label: "Modalità di trasferimento", value: "Passiva" },
      { id: "encryption", label: "Crittografia", value: "FTP semplice, oppure FTPS (FTP esplicito su TLS). Per l'FTPS usa il nome host, non l'indirizzo IP." },
    ],
    switching: "Usavi già l'FTP di Photohawk? Nome utente, password e cartelle restano gli stessi. Cambia solo l'host con il tuo nuovo indirizzo.",
  },
  password: {
    heading: "Una password solo per l'FTP, se vuoi",
    body: "Invece di salvare la tua password di Photohawk in un programma FTP, fai clic su «Create FTP password» nel pannello. Funziona solo sul tuo server FTP, con la tua email come nome utente. Puoi copiarla, crearne una nuova (la vecchia smette di funzionare entro un minuto) o rimuoverla in qualsiasi momento. Anche la tua password di Photohawk continua a funzionare.",
  },
  folders: {
    heading: "Le tue cartelle",
    body: "Vedrai le stesse cartelle di prima:",
    path: "/Galleries/<gallery>/<collection>",
    points: [
      { id: "single", text: "Una galleria con una sola raccolta è semplicemente `/Galleries/<gallery>`." },
      { id: "workspaces", text: "Carichi in più di un'area di lavoro? Ognuna ha la sua cartella, con le sue gallerie dentro." },
      { id: "create", text: "Crea prima la galleria e le sue raccolte in Photohawk. Le tue foto compaiono nella galleria in pochi istanti, proprio come con l'uploader web." },
    ],
  },
  off: {
    heading: "Si spegne da solo",
    body: "Il tuo server si spegne dopo 1 ora di inattività, quindi non devi ricordarti niente. Il pannello mostra un conto alla rovescia.",
    points: [
      { id: "keep", text: "«Keep it running» fa ripartire il conto alla rovescia senza caricare niente." },
      { id: "stop", text: "«Stop» lo spegne subito quando hai finito." },
      { id: "caps", text: "Per sicurezza si spegne anche dopo 12 ore senza caricamenti in corso, e comunque dopo 24 ore al massimo." },
      { id: "again", text: "Riavvialo quando vuoi, con lo stesso indirizzo e le stesse credenziali." },
    ],
  },
  regions: {
    heading: "Regioni",
    body: "Puoi avviare il tuo server in Europa (Irlanda), negli Stati Uniti orientali (Virginia del Nord) o in Asia-Pacifico (Malesia). Viene scelta per te la regione più vicina, e puoi cambiarla prima dell'avvio.",
  },
  filezilla: {
    heading: "Esempio: configurare FileZilla",
    subtitle: "Gli altri programmi FTP usano le stesse impostazioni, a volte con nomi un po' diversi.",
    steps: [
      { id: "site", text: "Apri «File → Site Manager» e fai clic su «New site»." },
      { id: "protocol", text: "Protocollo: «FTP – File Transfer Protocol»." },
      { id: "host", text: "Host: il tuo indirizzo, ad es. `swift-heron-4821.ftp.photohawk.com`. Porta: `21`." },
      { id: "tls", text: "Crittografia: «Use explicit FTP over TLS if available»." },
      { id: "login", text: "Tipo di accesso: «Normal». Utente: la tua email di Photohawk. Password: la tua password FTP o quella di Photohawk." },
      { id: "passive", text: "Nella scheda «Transfer Settings» scegli «Passive». Fai clic su «Connect», poi apri `Galleries`." },
    ],
    tip: "Devi inviare una cartella grande? FileZilla invia 2 file alla volta di default, e non più di 10. Puoi alzare l'impostazione dei trasferimenti simultanei fino a 10 («Settings → Transfers»), oppure usare l'uploader web, che ne invia molti di più alla volta.",
  },
  trouble: {
    heading: "Se qualcosa non funziona",
    items: [
      { id: "connect", title: "Non riesci a connetterti", body: "Controlla che il pannello indichi «Ready». Se il nome host è stato appena creato, usa l'indirizzo IP per un minuto." },
      { id: "listing", title: "Connesso, ma l'elenco delle cartelle si blocca", body: "Attiva la modalità passiva." },
      { id: "logins", title: "“Too many failed logins”", body: "Aspetta 15 minuti, poi controlla il nome utente (la tua email) e la password." },
    ],
  },
  faq: {
    heading: "Domande",
    items: [
      {
        q: "Perché il mio server si è spento?",
        a: "Si spegne dopo un'ora di inattività, così non resta mai acceso quando non lo usi. Riavvialo quando vuoi: indirizzo e credenziali restano gli stessi. Per tenerlo acceso senza caricare niente, fai clic su «Keep it running». Si spegne anche dopo 12 ore senza caricamenti in corso, e comunque dopo 24 ore al massimo.",
      },
      {
        q: "Due persone possono avere un server nello stesso momento?",
        a: "Sì. Tutte le persone della tua area di lavoro possono avviare il proprio server, uno a testa, con il proprio indirizzo. Solo tu puoi accedere al tuo.",
      },
      {
        q: "È sicuro?",
        a: "Sì. Con l'FTPS (FTP su TLS) sul tuo nome host, la connessione è crittografata con un certificato affidabile. Sul tuo server funzionano solo le tue credenziali, e dopo ripetuti tentativi di accesso falliti il server blocca quella connessione per 15 minuti.",
      },
      {
        q: "Costa qualcosa in più?",
        a: "No. Il tuo server FTP è incluso in tutti i piani.",
      },
      {
        q: "È incluso in tutti i piani?",
        a: "Sì, anche in Lite. Per i caricamenti di tutti i giorni l'uploader web o Photohawk Studio sono più veloci; il tuo server FTP c'è quando l'FTP è indispensabile.",
      },
      {
        q: "Cosa succede al vecchio FTP condiviso?",
        a: "I server FTP condivisi sempre attivi chiudono il 1° novembre 2026 e fino ad allora funzionano come prima. Per continuare a usare l'FTP, avvia il tuo server e sostituisci l'host nel tuo programma FTP con il tuo nuovo indirizzo. Nome utente, password e cartelle restano gli stessi.",
      },
    ],
  },
  cta: {
    heading: "Pronto quando lo sei tu",
    body: "Per i caricamenti di tutti i giorni usa l'uploader web. Quando l'FTP è indispensabile, il tuo server è a portata di clic.",
    primary: "Apri l'uploader web",
    secondary: "Avvia il tuo server FTP",
  },
};

export default it;
